import type { ApolloClient } from '@apollo/client';
import serverSideCalculations from 'shared/graphql/calculations/mutations/server-side-calculations.gql';

import { ecommCalculationsInput } from '../helpers/calculations/helpers';
import type { EcommCalculationsInputData } from '../helpers/calculations/types';
import type { CalculationPriceService } from '../constants/calculations';

type LoggerFunction = (message: string, context?: Record<string, unknown>) => void;
type Logger = { error: LoggerFunction };

// The legacy calculations mutation's response shape is intentionally not typed
// against a specific app's generated GraphQL types here — this file is
// consumed via the `packages/shared` symlink by whichever app calls it, and
// this function only ever checks the response for truthiness before relaying
// it unchanged, never destructuring specific fields.
type LegacyCalculationResults = Record<string, unknown>;

const handleLegacyCalculationsRequest = async ({
  apolloClient,
  legacyInput,
}: {
  apolloClient: ApolloClient<object>;
  legacyInput: ReturnType<typeof ecommCalculationsInput>;
}) =>
  apolloClient.mutate({
    mutation: serverSideCalculations,
    variables: { input: legacyInput },
  });

export async function serverSideComputations(
  data: EcommCalculationsInputData,
  logger: Logger,
  apolloClient: ApolloClient<object> | null,
  priceService: CalculationPriceService
): Promise<LegacyCalculationResults> {
  if (!data.dispensary || !data.cart || !apolloClient) {
    logger.error('Missing required data for legacy calculations', {
      hasDispensary: !!data.dispensary,
      hasCart: !!data.cart,
      hasApolloClient: !!apolloClient,
      dispensaryId: data.dispensary?.id,
      priceService,
    });
    return {};
  }

  let response;

  const legacyInput = ecommCalculationsInput(data, priceService);

  try {
    response = await handleLegacyCalculationsRequest({
      apolloClient,
      legacyInput,
    });
  } catch (error) {
    logger.error('Error in legacy calculations API call', {
      dispensaryId: data.dispensary?.id,
      priceService,
      // Cast (not `instanceof` narrowing) to exactly preserve the original
      // untyped behavior for a non-Error throw: `.message` on a non-object
      // safely evaluates to `undefined` at runtime either way.
      error: (error as Error)?.message,
    });
  }

  // NOTE: pre-existing latent bug, not fixed here (no test locks in either
  // behavior, so preserving the original is safest for a type-only
  // migration): if the try block above throws, `response` stays `undefined`
  // and this line throws a TypeError instead of hitting the graceful
  // "Empty response" branch below. Flagged in the tracker for a human.
  // The `!` is genuinely needed for apps/api's stricter tsc (it flags
  // `response` as possibly undefined here; apps/admin and apps/marketplace
  // don't), even though this app's own lint sees it as locally redundant.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-type-assertion
  const legacyOutput = response!.data?.serverSideCalculations;

  if (!legacyOutput) {
    logger.error('Empty response from legacy calculations API', {
      dispensaryId: data.dispensary?.id,
      priceService,
    });

    return {};
  }

  return legacyOutput;
}
